import React from 'react';
import { ArrowRightIcon, CheckIcon, BookOpenIcon, MicIcon, CalendarIcon, LayersIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function MeetingsAndFacilitation() {
  const {
    t
  } = useLanguage();
  const signs = t('meetingsAndFacilitation.signs') as string[];
  const whatWeDoItems = t('meetingsAndFacilitation.whatWeDoItems') as string[];
  return <PageTransition data-id="element-1543">
      <div className="pt-12 pb-24" data-id="element-1544">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1545">
          <DecorativeShape type="blob" color="bg-yellow-200/40" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-1546" />
          <Animate className="max-w-3xl" data-id="element-1547">
            <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 font-medium rounded-full text-sm mb-6" data-id="element-1548">
              {t('meetingsAndFacilitation.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1549">
              {t('meetingsAndFacilitation.title')}
              <br data-id="element-1550" />
              <span className="text-yellow-600" data-id="element-1551">{t('meetingsAndFacilitation.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1552">
              {t('meetingsAndFacilitation.intro1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1553">
              {t('meetingsAndFacilitation.intro2')}
            </p>
            <Link to="/contact" data-id="element-1554">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1556" />} data-id="element-1555">
                {t('meetingsAndFacilitation.heroCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* Signs */}
        <section className="bg-cream py-20 mb-20" data-id="element-1557">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1558">
            <Animate className="mb-12" data-id="element-1559">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1560">
                {t('meetingsAndFacilitation.signsTitle')}
              </h2>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1561">
              {signs.map((sign, i) => <Animate key={i} delay={i * 0.05} data-id="element-1562">
                  <div className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm" data-id="element-1563">
                    <div className="mt-0.5 p-1 rounded-full bg-yellow-100 text-yellow-600 flex-shrink-0" data-id="element-1564">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1565" />
                    </div>
                    <span className="text-gray-700" data-id="element-1566">{sign}</span>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* What we work on */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1567">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1568">
            <Animate data-id="element-1569">
              <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-1570">
                {t('meetingsAndFacilitation.whatWeDoTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1571">
                {t('meetingsAndFacilitation.whatWeDoP1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1572">
                {t('meetingsAndFacilitation.whatWeDoP2')}
              </p>
            </Animate>
            <Animate delay={0.1} data-id="element-1573">
              <div className="space-y-3" data-id="element-1574">
                {whatWeDoItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1575">
                    <div className="mt-1 p-1 rounded-full bg-yellow-100 text-yellow-600 flex-shrink-0" data-id="element-1576">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1577" />
                    </div>
                    <span className="text-gray-700" data-id="element-1578">{item}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        {/* Ways to work together */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1579">
          <Animate className="mb-12" data-id="element-1580">
            <h2 className="text-3xl font-bold text-charcoal" data-id="element-1581">
              {t('meetingsAndFacilitation.waysTitle')}
            </h2>
            <p className="text-gray-500 mt-2" data-id="element-1582">{t('meetingsAndFacilitation.waysSubtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1583">
            {[{
            icon: <BookOpenIcon size={24} className="text-coral" data-id="element-1584" />,
            typeBg: 'bg-coral/10 text-coral',
            typeKey: 'courseLabel',
            titleKey: 'course1Title',
            descKey: 'course1Desc',
            path: '/services/courses/better-online-meetings'
          }, {
            icon: <BookOpenIcon size={24} className="text-purple-600" data-id="element-1585" />,
            typeBg: 'bg-purple-100 text-purple-700',
            typeKey: 'courseLabel',
            titleKey: 'course2Title',
            descKey: 'course2Desc',
            path: '/services/courses/better-online-workshops'
          }, {
            icon: <LayersIcon size={24} className="text-teal" data-id="element-1586" />,
            typeBg: 'bg-teal/10 text-teal',
            typeKey: 'workshopLabel',
            titleKey: 'workshop1Title',
            descKey: 'workshop1Desc',
            path: '/services/workshops'
          }, {
            icon: <MicIcon size={24} className="text-yellow-600" data-id="element-1587" />,
            typeBg: 'bg-yellow-100 text-yellow-700',
            typeKey: 'talkLabel',
            titleKey: 'talk1Title',
            descKey: 'talk1Desc',
            path: '/services/talks'
          }].map((item, i) => <Animate key={i} delay={i * 0.1} data-id="element-1588">
                <Card hoverEffect className="h-full p-6 flex flex-col" data-id="element-1589">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1590">
                    {item.icon}
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${item.typeBg}`} data-id="element-1591">
                      {t(`meetingsAndFacilitation.${item.typeKey}`)}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg text-charcoal mb-2" data-id="element-1592">
                    {t(`meetingsAndFacilitation.${item.titleKey}`)}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4" data-id="element-1593">
                    {t(`meetingsAndFacilitation.${item.descKey}`)}
                  </p>
                  <Link to={item.path} data-id="element-1594">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRightIcon size={16} data-id="element-1596" />} data-id="element-1595">
                      {t('meetingsAndFacilitation.learnMore')}
                    </Button>
                  </Link>
                </Card>
              </Animate>)}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1597">
          <Animate data-id="element-1598">
            <Card className="p-12 bg-gradient-to-br from-yellow-50 to-amber-50 border-0" data-id="element-1599">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-1600">
                <CalendarIcon size={32} className="text-yellow-600" data-id="element-1601" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1602">{t('meetingsAndFacilitation.ctaTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1603">{t('meetingsAndFacilitation.ctaDesc')}</p>
              <Link to="/contact" data-id="element-1604">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-1606" />} data-id="element-1605">
                  {t('meetingsAndFacilitation.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}